import { type ComponentProps } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button-v25/new';
import Heading from '@atlaskit/heading';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CrossIcon from '@atlaskit/icon/core/cross';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import SurveyIcon from '@atlaskit/icon-lab/core/survey';
import Lozenge from '@atlaskit/lozenge-next/new';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import {
	surveyChoiceAppearances,
	surveyChoiceLabels,
	type SurveyResponse,
	type SurveyResponseAnswers,
} from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';
import { JsmPlaygroundPersonAvatar } from '../jsm-playground-person-avatar';

import { surveyRespondentAvatars } from './survey-respondent-avatars';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const CloseIcon: IconButtonIcon = () => <CrossIcon label="" size="medium" />;
const ExpandIcon: IconButtonIcon = () => <GrowDiagonalIcon label="" size="medium" />;

const noop = () => {};

export type SurveyResponsePanelProps = {
	response: SurveyResponse & { answers: SurveyResponseAnswers };
	onClose: () => void;
};

/**
 * One response opened beside the Responses table: who answered and when, then each question with
 * its answer. Keyed on the response by its caller, so switching rows swaps the content with a short
 * fade rather than rewriting text in place.
 */
export const SurveyResponsePanel = ({ response, onClose }: SurveyResponsePanelProps) => {
	const intl = useIntl();
	const { id, respondent, satisfaction, answers } = response;

	return (
		<section
			css={panelStyles}
			aria-label={intl.formatMessage(messages.responsePanelLabel, { name: respondent })}
			data-testid="jsm-hr-survey-response-panel"
		>
			<div css={panelHeaderStyles}>
				<SurveyIcon color={token('color.icon')} label="" />
				<span css={panelTitleStyles}>
					<Heading size="small" as="h3">
						{intl.formatMessage(messages.surveyResponse)}
					</Heading>
				</span>
				{/* There is no full-screen response view, so expand is drawn but inert. */}
				<span aria-hidden="true" css={inertControlStyles}>
					<IconButton
						appearance="subtle"
						icon={ExpandIcon}
						label={intl.formatMessage(messages.expandResponse)}
						onClick={noop}
						tabIndex={-1}
					/>
				</span>
				<IconButton
					icon={CloseIcon}
					label={intl.formatMessage(messages.closeResponsePanel)}
					onClick={onClose}
				/>
			</div>

			<div css={panelContentStyles}>
				<div css={metaStyles}>
					<span css={personStyles}>
						<JsmPlaygroundPersonAvatar src={surveyRespondentAvatars[id]} />
						<Text weight="medium">{respondent}</Text>
					</span>
					<span css={dateChipStyles}>
						<CalendarIcon color={token('color.icon.subtle')} label="" size="small" />
						{intl.formatMessage(answers.submitted)}
					</span>
				</div>

				<dl css={answerListStyles}>
					<div css={answerStyles}>
						<dt css={questionStyles}>
							<Text color="color.text.subtle">{intl.formatMessage(messages.questionOneShort)}</Text>
							<Text weight="bold">{intl.formatMessage(messages.questionOne)}</Text>
						</dt>
						<dd css={answerValueStyles}>
							<Lozenge appearance={surveyChoiceAppearances[satisfaction]}>
								{intl.formatMessage(surveyChoiceLabels[satisfaction])}
							</Lozenge>
						</dd>
					</div>
					<div css={[answerStyles, ruledAnswerStyles]}>
						<dt css={questionStyles}>
							<Text color="color.text.subtle">{intl.formatMessage(messages.questionTwoShort)}</Text>
							<Text weight="bold">{intl.formatMessage(messages.questionTwo)}</Text>
						</dt>
						<dd css={answerValueStyles}>
							<Text>{intl.formatMessage(answers.rewarding)}</Text>
						</dd>
					</div>
				</dl>
			</div>
		</section>
	);
};

const swapIn = keyframes({
	from: { opacity: 0, transform: 'translateY(4px)' },
	to: { opacity: 1, transform: 'none' },
});

const panelStyles = css({
	display: 'flex',
	flexDirection: 'column',
	boxSizing: 'border-box',
	width: '100%',
	height: '100%',
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
	overflowX: 'hidden',
	overflowY: 'auto',
});

const panelHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
	gap: token('space.100'),
	padding: token('space.200'),
	borderBlockEnd: `${token('border.width', '1px')} solid ${token('color.border')}`,
});

const panelTitleStyles = css({
	flex: '1 1 auto',
	minWidth: 0,
});

const inertControlStyles = css({
	display: 'inline-flex',
	pointerEvents: 'none',
});

const panelContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	animation: `${swapIn} 180ms cubic-bezier(0.2, 0, 0, 1)`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const metaStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.150'),
	paddingBlockStart: token('space.200'),
	paddingInline: token('space.200'),
});

const personStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const dateChipStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	boxSizing: 'border-box',
	height: '24px',
	paddingInline: token('space.100'),
	border: `${token('border.width', '1px')} solid ${token('color.border')}`,
	borderRadius: token('radius.small'),
	color: token('color.text'),
	font: token('font.body'),
	whiteSpace: 'nowrap',
});

const answerListStyles = css({
	display: 'flex',
	flexDirection: 'column',
	margin: 0,
	paddingInline: token('space.200'),
	paddingBlockEnd: token('space.100'),
});

const answerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	paddingBlock: token('space.200'),
});

const ruledAnswerStyles = css({
	borderBlockStart: `${token('border.width', '1px')} solid ${token('color.border')}`,
});

const questionStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	margin: 0,
});

const answerValueStyles = css({
	margin: 0,
});
